// src/auth/AuthContext.tsx
import React, {
	createContext,
	PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from 'react';
import { User } from 'oidc-client-ts';
import { userManager } from './AuthManager';

interface AuthContextValue {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	login: () => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren<unknown>) => {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		userManager.getUser().then((existingUser) => {
			setUser(existingUser);
			setIsLoading(false);
		});

		const onUserLoaded = (loadedUser: User) => setUser(loadedUser);
		const onUserUnloaded = () => setUser(null);
		const onSilentRenewError = (error: Error) => {
			console.error('Silent renew failed:', error);
			userManager.signinRedirect();
		};

		userManager.events.addUserLoaded(onUserLoaded);
		userManager.events.addUserUnloaded(onUserUnloaded);
		userManager.events.addSilentRenewError(onSilentRenewError);

		return () => {
			userManager.events.removeUserLoaded(onUserLoaded);
			userManager.events.removeUserUnloaded(onUserUnloaded);
			userManager.events.removeSilentRenewError(onSilentRenewError);
		};
	}, []);

	const login = () => userManager.signinRedirect();
	const logout = async () => {
		// First, revoke the refresh token via oidc-client-ts (this part works
		// against both Cognito and standards-compliant providers).
		try {
			await userManager.revokeTokens(['refresh_token']);
		} catch (e) {
			console.error('Token revocation failed (continuing logout):', e);
		}

		// Clear local session
		await userManager.removeUser();

		// --- PROVIDER-SPECIFIC: Cognito logout ---
		// Cognito does not implement OIDC RP-Initiated Logout, so signoutRedirect()
		// builds a URL Cognito doesn't understand. We manually construct Cognito's
		// expected logout URL: {endpointBase}/logout?client_id=X&logout_uri=Y
		//
		// For Keycloak, this whole block should be replaced with the standard
		// userManager.signoutRedirect(), which Keycloak supports correctly:
		// const logout = async () => {
		// 	await userManager.signoutRedirect();
		// };
		const oidcEndpointBase = process.env.REACT_APP_OIDC_ENDPOINT_BASE!;
		const clientId = process.env.REACT_APP_OIDC_CLIENT_ID!;
		const logoutUri = process.env.REACT_APP_BASE_URL!;

		const logoutUrl = new URL(`${oidcEndpointBase}/logout`);
		logoutUrl.searchParams.set('client_id', clientId);
		logoutUrl.searchParams.set('logout_uri', logoutUri);

		window.location.href = logoutUrl.toString();
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				isLoading,
				isAuthenticated: !!user && !user.expired,
				login,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
	return ctx;
};
